import { useState, useRef, useEffect } from "react";
import confetti from "canvas-confetti";
import { Button } from "./ui/button";
import { PartyPopper, Sparkles } from "lucide-react";

// ==================== MUSIC ====================
// Play the real victory music file with fallback to synthesized anthem
const playVictoryMusic = () => {
  try {
    const audio = new Audio(`${process.env.PUBLIC_URL || ""}/sounds/victory.mp3`);
    audio.volume = 0.85;
    audio.preload = "auto";
    const playPromise = audio.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(() => playSynthesizedFallback());
    }
    // Stop after 12 seconds in case file is longer
    setTimeout(() => {
      try {
        audio.pause();
        audio.currentTime = 0;
      } catch (e) { /* ignore */ }
    }, 12000);
  } catch (e) {
    playSynthesizedFallback();
  }
};

// Fallback: Champions League-style triumphant anthem (synthesized, ~11s)
const playSynthesizedFallback = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const masterGain = ctx.createGain();
    masterGain.gain.value = 0.32;
    masterGain.connect(ctx.destination);

    const t0 = ctx.currentTime;

    const playNote = (freq, start, dur, vol = 0.4, type = "triangle") => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, t0 + start);
      gain.gain.setValueAtTime(0, t0 + start);
      gain.gain.linearRampToValueAtTime(vol, t0 + start + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + start + dur);
      osc.connect(gain).connect(masterGain);
      osc.start(t0 + start);
      osc.stop(t0 + start + dur + 0.1);
    };

    const G5 = 783.99, B5 = 987.77, D6 = 1174.66, G6 = 1567.98, C7 = 2093.00;
    const G3 = 196.00;

    playNote(G3, 0.0, 3.0, 0.25, "sine");
    playNote(G5, 0.0, 0.4, 0.45);
    playNote(B5, 0.3, 0.4, 0.45);
    playNote(D6, 0.6, 0.4, 0.5);
    playNote(G6, 0.9, 0.8, 0.55);
    playNote(G6, 2.0, 0.4, 0.55);
    playNote(C7, 2.5, 0.8, 0.6);

    setTimeout(() => { try { ctx.close(); } catch (e) { /* ignore */ } }, 5000);
  } catch (e) { /* ignore */ }
};

// ==================== CONFETTI CHOREOGRAPHY ====================
const fireSpectacularShow = () => {
  const TOTAL_DURATION = 10500;
  const end = Date.now() + TOTAL_DURATION;

  const GOLD = ["#FFD700", "#FFC107", "#FFA500", "#FFB300", "#FFEB3B", "#F59E0B"];
  const RAINBOW = ["#22c55e", "#3b82f6", "#f59e0b", "#ec4899", "#a855f7", "#ef4444", "#06b6d4"];

  // Helper: build emoji shape
  const emojiShape = (emoji) => {
    try { return confetti.shapeFromText({ text: emoji, scalar: 2 }); } catch { return null; }
  };

  const VICTORY_EMOJIS = ["🏆", "🥇", "⭐", "💎", "👑", "🎉", "🎊", "💰", "📈", "🚀", "🔥", "✨"];
  const NUMBER_ONE = ["1️⃣", "🥇", "🏆"];
  const victoryShapes = VICTORY_EMOJIS.map(emojiShape).filter(Boolean);
  const numberOneShapes = NUMBER_ONE.map(emojiShape).filter(Boolean);

  // ========= INITIAL MEGA BURST (t=0) =========
  confetti({
    particleCount: 250,
    spread: 160,
    startVelocity: 70,
    origin: { x: 0.5, y: 0.55 },
    colors: [...GOLD, ...RAINBOW],
  });

  // Gold star burst
  setTimeout(() => {
    confetti({
      particleCount: 120,
      spread: 120,
      startVelocity: 50,
      origin: { x: 0.5, y: 0.5 },
      colors: GOLD,
      shapes: ["star"],
      scalar: 1.4,
    });
  }, 100);

  // ========= SIDE CANNONS (t=300ms) =========
  setTimeout(() => {
    confetti({ particleCount: 150, angle: 55, spread: 70, startVelocity: 65, origin: { x: 0, y: 0.7 }, colors: GOLD });
    confetti({ particleCount: 150, angle: 125, spread: 70, startVelocity: 65, origin: { x: 1, y: 0.7 }, colors: GOLD });
  }, 300);

  // ========= CONTINUOUS GOLD GLITTER RAIN (full duration) =========
  (function goldRain() {
    confetti({
      particleCount: 8,
      angle: 90,
      spread: 80,
      startVelocity: 25,
      gravity: 0.6,
      ticks: 400,
      origin: { x: Math.random(), y: -0.05 },
      colors: GOLD,
      shapes: ["circle", "square"],
      scalar: 0.7,
    });
    if (Date.now() < end) requestAnimationFrame(goldRain);
  })();

  // ========= VICTORY EMOJI RAIN (1.5s - 7s) =========
  if (victoryShapes.length > 0) {
    const emojiEnd = Date.now() + 5500;
    setTimeout(() => {
      (function emojiRain() {
        confetti({
          particleCount: 3,
          angle: 90,
          spread: 65,
          startVelocity: 28,
          gravity: 0.8,
          ticks: 500,
          origin: { x: Math.random(), y: -0.05 },
          shapes: victoryShapes,
          scalar: 2.2,
        });
        if (Date.now() < emojiEnd) setTimeout(emojiRain, 140);
      })();
    }, 1500);
  }

  // ========= NUMBER ONE BURSTS (every 1.5s from 2s to 9s) =========
  if (numberOneShapes.length > 0) {
    [2000, 3500, 5000, 6500, 8000, 9500].forEach((delay) => {
      setTimeout(() => {
        confetti({
          particleCount: 30,
          spread: 110,
          startVelocity: 45,
          origin: { x: 0.3 + Math.random() * 0.4, y: 0.4 + Math.random() * 0.2 },
          shapes: numberOneShapes,
          scalar: 3,
          ticks: 250,
        });
      }, delay);
    });
  }

  // ========= FIREWORKS (every 800ms from 2s to 9s) =========
  const fireworks = [2000, 2800, 3600, 4400, 5200, 6000, 6800, 7600, 8400, 9200];
  fireworks.forEach((delay, i) => {
    setTimeout(() => {
      const colors = i % 2 === 0 ? GOLD : RAINBOW;
      confetti({
        particleCount: 80,
        spread: 360,
        startVelocity: 35,
        origin: { x: 0.15 + Math.random() * 0.7, y: 0.2 + Math.random() * 0.3 },
        colors,
        shapes: ["star", "circle"],
        scalar: 1.2,
        ticks: 200,
      });
    }, delay);
  });

  // ========= GOLD STARS STREAM (3s - 10s) =========
  setTimeout(() => {
    const starsEnd = Date.now() + 7000;
    (function goldStars() {
      confetti({
        particleCount: 4,
        angle: 60 + Math.random() * 60,
        spread: 50,
        startVelocity: 40,
        origin: { x: Math.random(), y: 1 },
        colors: GOLD,
        shapes: ["star"],
        scalar: 1.3,
        gravity: 0.4,
        drift: (Math.random() - 0.5) * 2,
      });
      if (Date.now() < starsEnd) setTimeout(goldStars, 180);
    })();
  }, 3000);

  // ========= GRAND FINALE (t=8.5s) =========
  setTimeout(() => {
    confetti({
      particleCount: 400,
      spread: 180,
      startVelocity: 80,
      origin: { x: 0.5, y: 0.6 },
      colors: [...GOLD, ...RAINBOW],
      shapes: ["star", "circle", "square"],
      scalar: 1.5,
      ticks: 400,
    });
    // Left + right mega cannons
    confetti({ particleCount: 200, angle: 60, spread: 90, startVelocity: 80, origin: { x: 0, y: 0.9 }, colors: GOLD, shapes: ["star"], scalar: 1.5 });
    confetti({ particleCount: 200, angle: 120, spread: 90, startVelocity: 80, origin: { x: 1, y: 0.9 }, colors: GOLD, shapes: ["star"], scalar: 1.5 });
  }, 8500);
};

// ==================== BIG CENTER OVERLAY ====================
const VictoryOverlay = () => {
  const [phase, setPhase] = useState(0); // 0..3 different visuals
  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 100),
      setTimeout(() => setPhase(2), 3200),
      setTimeout(() => setPhase(3), 6400),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  const content = phase === 1
    ? { big: "🏆", small: "VITTORIA!", sub: "Tutti gli indicatori in crescita" }
    : phase === 2
      ? { big: "🥇", small: "#1", sub: "Performance eccellente" }
      : phase === 3
        ? { big: "🚀", small: "BOOM!", sub: "Continua così!" }
        : null;

  if (!content) return null;

  return (
    <div
      key={phase}
      className="fixed inset-0 pointer-events-none z-[9999] flex flex-col items-center justify-start pt-[6vh]"
      style={{ animation: "celebrationPop 3.2s ease-out forwards" }}
    >
      <div className="text-center px-4 max-w-full" style={{ filter: "drop-shadow(0 0 30px rgba(255, 215, 0, 0.8))" }}>
        <div
          className="leading-[0.85] select-none"
          style={{
            fontSize: "clamp(90px, 20vw, 220px)",
            animation: "celebrationSpin 3s ease-in-out",
          }}
        >
          {content.big}
        </div>
        <div
          className="font-black mt-2 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500 text-transparent bg-clip-text leading-none"
          style={{
            fontSize: "clamp(48px, 10vw, 110px)",
            WebkitTextStroke: "2px rgba(255,255,255,0.3)",
            textShadow: "0 0 40px rgba(255, 215, 0, 1)",
          }}
        >
          {content.small}
        </div>
        <div
          className="font-semibold text-white mt-3"
          style={{
            fontSize: "clamp(16px, 2.5vw, 28px)",
            textShadow: "0 2px 20px rgba(0,0,0,0.9), 0 0 10px rgba(0,0,0,0.8)",
          }}
        >
          {content.sub}
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN BUTTON ====================
export const CelebrationButton = ({ label = "Festeggia con il cliente!", subtitle }) => {
  const [celebrating, setCelebrating] = useState(false);
  const timerRef = useRef(null);

  const handleClick = () => {
    if (celebrating) return;
    setCelebrating(true);
    playVictoryMusic();
    fireSpectacularShow();
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCelebrating(false), 11000);
  };

  return (
    <>
      <div className="relative overflow-hidden rounded-2xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 via-pink-50 to-emerald-50 p-5 shadow-md animate-pulse-slow">
        <div className="absolute -top-4 -right-4 opacity-20 text-[120px] rotate-12 select-none pointer-events-none">🎉</div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 relative">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-pink-500 flex items-center justify-center shadow-lg">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            <div>
              <div className="font-bold text-base text-slate-900">Tutti gli indicatori sono in crescita!</div>
              <div className="text-xs text-slate-700 mt-0.5">
                {subtitle || "Ricavi, margine di contribuzione, EBITDA e utile sono tutti positivi e in aumento rispetto al mese precedente e all'anno precedente."}
              </div>
            </div>
          </div>
          <Button
            onClick={handleClick}
            disabled={celebrating}
            className="bg-gradient-to-r from-amber-500 via-pink-500 to-emerald-500 hover:from-amber-600 hover:via-pink-600 hover:to-emerald-600 text-white font-bold shadow-lg border-0 text-sm px-5 py-5 transition-transform hover:scale-105"
            data-testid="celebrate-client-btn"
          >
            <PartyPopper className="h-5 w-5 mr-2" />
            {celebrating ? "In festa..." : label}
          </Button>
        </div>
      </div>
      {celebrating && <VictoryOverlay />}
    </>
  );
};

export default CelebrationButton;
